// Blueprint: forge/blueprint/frontend/architecture/001-shell-composition.md
import { Page, Text } from '@servicetitan/anvil2';
import { FC, useRef } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Provider, useDependencies } from '@servicetitan/react-ioc';
import { getQueryClient } from '@servicetitan/tanstack-query-mobx/composable';
import { observer } from 'mobx-react';

import { Sidebar } from './components/Sidebar';
import { HomeModule } from './modules/home/HomeModule';
import { AppStore } from './stores/app.store';
import { ModulePage } from './components/ModulePage';
import { LoadingSpinner } from './components/loading-spinner/LoadingSpinner';
import { NoAccessMessage } from './components/no-access/NoAccessMessage';
import { usePrefetchRouteModules } from './hooks/use-prefetch-route-modules';
import { ModuleSettingsOverride, ModuleSettingsOverrideToken, RootOrgIdToken } from './tokens';
import { createApiProviders } from './api/api-client';
import { useScrollportMetrics } from './layout/use-scrollport-metrics';

import * as Styles from './layout/org-hub-page.module.scss';

interface OrgHubProps {
    /** Standalone: from `/me`. Embedded: host prop. Null/invalid → fail-closed (no registry reads). */
    rootOrgId?: string | null;
    /** Standalone default `''` (same-origin). Embedded hosts usually rely on the public MFE default. */
    apiBaseUrl?: string;
    /** Merged over every child module's registry settings. Empty unless the host routes APIs itself. */
    moduleSettingsOverride?: ModuleSettingsOverride;
}

export const OrgHubUnwrapped: FC = observer(() => {
    const [appStore] = useDependencies(AppStore);
    usePrefetchRouteModules();

    /*
     * Anvil2's `Page` sizes itself and pins its sticky sidebar against the viewport, taking
     * the host's `--offset` as the height of the chrome above it. That is only right while
     * the document is the scroller: embedded in the monolith the Page lives in a host pane
     * that starts below the host header and banners and is itself what scrolls, so the
     * Page overflows the pane and sticky content counts the header twice.
     * `useScrollportMetrics` measures that pane and `org-hub-page.module.scss` derives the
     * Page's height and sticky inset from it — a no-op in the standalone, where the
     * document scrolls — and publishes the height as `--a2-mod-page-height` for modules
     * that need to fill the Page (the GUM wizards). The module renders into its own box
     * because below Anvil2's container breakpoint the sidebar is a row above it, and the
     * height a module can fill is shorter by that row — the stylesheet takes it off there.
     */
    const hostOffsetScopeRef = useRef<HTMLDivElement>(null);
    useScrollportMetrics(hostOffsetScopeRef);

    return (
        <div ref={hostOffsetScopeRef} className={Styles.hostOffsetScope}>
            <Page className={Styles.page}>
                <Page.Sidebar localStorageKey="org-hub-sidebar">
                    <Page.SidebarHeader>
                        <Text variant="headline" el="h2">
                            ORG Hub
                        </Text>
                    </Page.SidebarHeader>
                    {!appStore.hasError && <Sidebar />}
                </Page.Sidebar>

                <div className={Styles.module}>
                    {appStore.notEnabled ? (
                        <div className={Styles.noAccess}>
                            <NoAccessMessage orgName={appStore.deniedOrgName} />
                        </div>
                    ) : appStore.hasError ? (
                        <HomeModule />
                    ) : (
                        <Switch>
                            {appStore.routeModules.map(m => (
                                <Route key={m.id} path={m.route}>
                                    <ModulePage module={m} />
                                </Route>
                            ))}
                            <Route>
                                {/*
                                 * `/` and any path no module claims → first navigable module; HomeModule
                                 * only if the registry has none. Last, so module prefixes match first.
                                 */}
                                {appStore.landingPath ? (
                                    <Redirect to={appStore.landingPath} />
                                ) : (
                                    <HomeModule />
                                )}
                            </Route>
                        </Switch>
                    )}
                </div>
            </Page>
        </div>
    );
});

export const OrgHub: FC<OrgHubProps> = ({
    rootOrgId = null,
    apiBaseUrl = '',
    moduleSettingsOverride = {},
}) => {
    return (
        <Provider
            singletons={[
                AppStore,
                /*
                 * Holds a reference to the shared 'page' QueryClient for as long as the shell is
                 * mounted. This Provider sits above the router, so the client survives a module
                 * change; the mounted MFEs also take 'page' refs, but they unmount one at a time
                 * (ModulePage keys <Loader> by module id) and the client is ref-counted — without
                 * this holder the count would hit zero on every navigation and the cache would be
                 * dropped.
                 *
                 * The instance itself crosses bundle boundaries — the MFEs are loaded from the CDN
                 * and consume this object through their own copy of the library — so the shell and
                 * every GUM MFE must stay on the same @tanstack/query-core (5.28.13 today, pinned by
                 * tanstack-query-mobx's ~5.28.4 range). The shared key is versioned by the wrapper
                 * (`page-1.0.0`), not by query-core, so a drift there would not be caught by the key.
                 *
                 * 'page' is window-scoped rather than Org-Hub-scoped: any other consumer on the
                 * same page calling getQueryClient('page') would share this client, and releasing the
                 * last ref clears every non-'app' entry. No such consumer exists today (checked across
                 * the monolith, IUM and org-structure MFEs) — revisit if one appears.
                 */
                getQueryClient('page'),
                { provide: RootOrgIdToken, useValue: rootOrgId },
                { provide: ModuleSettingsOverrideToken, useValue: moduleSettingsOverride },
                ...createApiProviders(rootOrgId, apiBaseUrl),
            ]}
            loadingFallback={<LoadingSpinner />}
        >
            <OrgHubUnwrapped />
        </Provider>
    );
};
